import { createSelector } from "reselect"
import { get, find, chain, map, filter, first, mergeWith, some, size, pick } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups } from "helpers/rollups"
import { positionSetContributorsSummaryText, sortContributors } from "helpers/contributors"
import { VIEW_TYPES } from "helpers/base"
import { getScreenName } from "modules/analyze-composite-exposure/selectors/file"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { aggregateExposuresFactors } from "helpers/factorClassification"
import { computeSelectedGroup } from "helpers/analyze"
import { createQueryStringSelector } from "selectors/queryString"
import { makeGroupSort } from "helpers/contributorsSort"
import { METRIC_TYPES } from "helpers/assets/constants"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []

export const getIsSensitivityContentSet = (_, props) => get(props, "isSensitivityContentSet")

export const getGroupBy = (_, props) => get(props, "currentGrouping.id")
export const getSelectedContributorCategory = (_, props) => props.selectedContributorCategory
const getAggregationFunction = (_, props) => props.aggregation
const getMustAggregateFactors = (_, props) => props.mustAggregateFactors
const getFactorsClassification = (_, props) => props.factorsClassification
const getCurrentGrouping = (_, props) => get(props, "currentGrouping")
const getPositionSetName = (_, props) => props.positionSetName
const getClassifications = (_, props) => props.classifications
const getContributorsData = state => get(state, "global.data.compositeExposureContributors.data.model.simulation.exposureContributors.0.contributors")
const getGroupedContributorsData = state => get(state, "global.data.compositeGroupedExposureContributors.data.model.simulation.groupedExposureContributors.0.groupedContributors")
const getSelectedGroupId = createQueryStringSelector("contributorGroup")
const getOrder = (_, props) => get(props, "sortOrder", EMPTY_OBJECT)
const getSelectedDate = (_, props) => props.selectedDate
const getShowNotHeld = (_, props) => props.showNotHeld
const getViewType = (_, props) => props.viewType
const getSelectedViewsFactors = (_, props) => props.selectedViewsFactors
const getMetricType = (_, props) => props.metricType

function getFactors(factors, mustAggregateFactors, styleAggregation, selectedViewFactors, factorsClassification, metricType) {
  if (!mustAggregateFactors) {
    return factors
  }

  const exposureField = metricType === METRIC_TYPES.CURRENCY_EXPOSURE ? "currencyExposure" : "exposure"
  let aggregatedFactors = aggregateExposuresFactors(factorsClassification, factors, styleAggregation, exposureField)
  if (size(selectedViewFactors)) {
    aggregatedFactors = chain(factors)
      .filter(factor => some(selectedViewFactors, item => item.id === factor.id))
      .concat(aggregatedFactors)
      .value()
  }
  return aggregatedFactors
}

function mapFactors(factors, exposureField) {
  return chain(factors)
    .keyBy("id")
    .mapValues(exposureField)
    .value()
}

function computeNotHeldBucket(contributors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, metricType) {
  const exposureField = metricType === METRIC_TYPES.CURRENCY_EXPOSURE ? "currencyExposure" : "exposure"

  const merge = (obj, source) => mergeWith(obj, source, (objValue, srcValue) =>
    !objValue ? srcValue : objValue + srcValue,
  )

  return chain(contributors)
    .filter(item => !!item.notHeld)
    .reduce((acc, value) => {
      acc.size += value.percentEquity
      const factors = getFactors(value.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification)
      merge(acc.factors, mapFactors(factors, exposureField))

      return acc
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      factors: {},
    })
    .value()
}

function mapExposureContributors(data, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, metricType) {
  const exposureField = metricType === METRIC_TYPES.CURRENCY_EXPOSURE ? "currencyExposure" : "exposure"

  const result = chain(data)
    .filter(item => showNotHeld || !item.notHeld)
    .map(security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
      const factors = getFactors(security.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification)
      return {
        ...pick(security, ["id", "country", "description", "currency", "assetClass", "assetSubclass", "notHeld", "bloombergTicker", "modelProviderId"]),
        classification: get(security, "classification.name"),
        size: security.percentEquity,
        factors: mapFactors(factors, exposureField),
        ticker,
        exchange,
      }
    })
    .value()

  if (!showNotHeld) {
    result.push(computeNotHeldBucket(data, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, metricType))
  }
  return result
}

export const getSelectedGroup = createSelector(
  [getSelectedGroupId, getGroupedContributorsData],
  (selectedGroupId, contributors) => computeSelectedGroup({ id: selectedGroupId }, contributors),
)

export const getIncludeNotHeld = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)

const getSelectedField = createSelector(
  [getSelectedContributorCategory],
  (selected) => get(selected, "field", "total"),
)

const getContributors = createSelector(
  [getContributorsData, getShowNotHeld, getMustAggregateFactors, getAggregationFunction, getSelectedViewsFactors, getFactorsClassification, getMetricType],
  mapExposureContributors,
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) =>
    filter(data, security => get(security, selectedField)),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY
    return sortContributors(data, selectedField, order)
  },
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getShowNotHeld, getMustAggregateFactors, getAggregationFunction, getSelectedViewsFactors, getFactorsClassification, getMetricType],
  (data, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, metricType) => {
    const exposureField = metricType === METRIC_TYPES.CURRENCY_EXPOSURE ? "currencyExposure" : "exposure"
    return map(data, group => {
      const factors = getFactors(group.factors, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, metricType)
      return {
        ...group,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(exposureField)
          .value(),
        contributors: mapExposureContributors(group.contributors, showNotHeld, mustAggregateFactors, styleAggregation, selectedViewsFactors, factorsClassification, metricType),
      }
    })
  },
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (data, selectedField, selectedGroup) => chain(data)
    .filter(group => !selectedGroup || selectedGroup.id === group.id)
    .map(group => ({
      ...group,
      contributors: filter(group.contributors, security => get(security, selectedField)),
    }))
    .value(),
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, selectedField),
        contributors: sortContributors(group.contributors, selectedField, order),
      }))
      .orderBy(...makeGroupSort(order))
      .value()
  },
)

const getAccountRollups = createSelector(
  [getClassifications],
  (classifications) => ({
    classifications,
  }),
)
export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getCurrentGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getShowNotHeld, getViewType],
  positionSetContributorsSummaryText,
)

export const getExcelData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelectedContributorCategory, getCurrentGrouping, getPositionSetName, getSelectedDate, getViewType, getSelectedGroup, getScreenName],
  (data, groupedData, selectedContributorCategory, contributorsGrouping, positionSetName, selectedDate, viewType, selectedGroup, screenName) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        type: screenName,
        portfolioName: positionSetName,
        selected: selectedContributorCategory,
        selectedDate,
        viewType,
        group,
        net: true,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        type: screenName,
        portfolioName: positionSetName,
        selected: selectedContributorCategory,
        selectedDate,
        viewType,
        contributorsGrouping,
        data: groupedData,
        net: true,
      })
    }

    return computeContributorsCSV({
      type: screenName,
      portfolioName: positionSetName,
      selected: selectedContributorCategory,
      selectedDate,
      viewType,
      data,
      net: true,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, selectedField)
    }

    return null
  },
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .map(group => ({
          ...group,
          value: get(group, selectedField),
        }))
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)
